import { MessageCircle, Mail, Globe, MapPin, Phone } from 'lucide-react';
import { CONTACT_INFO, CONSULTANT_NAME, CONSULTANT_PHOTO_URL, AGENCY_NAME } from '@/constants';

export default function Footer() {
  const contactLinks = [
    {
      icon: MessageCircle,
      label: 'WhatsApp',
      href: CONTACT_INFO.whatsapp,
      testId: 'footer-whatsapp',
    },
    {
      icon: Mail,
      label: 'Email',
      href: `mailto:${CONTACT_INFO.email}`,
      testId: 'footer-email',
    },
    {
      icon: Globe,
      label: 'Website',
      href: CONTACT_INFO.website,
      testId: 'footer-website',
    },
    {
      icon: MapPin,
      label: 'Morada',
      href: CONTACT_INFO.address,
      testId: 'footer-address',
    },
  ];

  return (
    <footer className="px-6 pt-8 pb-10" data-testid="footer">
      {/* Divider */}
      <div
        className="mx-auto mb-8"
        style={{
          width: '60px',
          height: '2px',
          background: 'linear-gradient(90deg, transparent, #B5985A, transparent)',
        }}
      />

      {/* Consultant card */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(145deg, #1A1A1A 0%, #111111 100%)',
          border: '1px solid rgba(181, 152, 90, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        }}
      >
        {/* Top accent bar */}
        <div
          style={{
            height: '3px',
            background: 'linear-gradient(90deg, #B5985A, #D4C18C, #B5985A)',
          }}
        />

        <div className="p-6">
          {/* Profile section */}
          <div className="flex items-center gap-4 mb-5">
            <div
              className="relative flex-shrink-0"
              style={{ width: '72px', height: '72px' }}
            >
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: 'linear-gradient(135deg, #B5985A, #D4C18C)',
                  padding: '2px',
                }}
              >
                <img
                  src={CONSULTANT_PHOTO_URL}
                  alt={CONSULTANT_NAME}
                  className="w-full h-full rounded-full object-cover"
                  style={{ border: '2px solid #1A1A1A' }}
                  data-testid="consultant-photo"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <p
                className="font-headline text-lg leading-tight"
                style={{ color: '#FFFFFF' }}
                data-testid="consultant-name"
              >
                {CONSULTANT_NAME}
              </p>
              <p
                className="font-subtitle text-sm mt-0.5"
                style={{ color: '#B5985A' }}
              >
                Consultor Imobiliário
              </p>
            </div>
          </div>

          {/* Contact details */}
          <div className="space-y-2.5 mb-5">
            <a
              href={`tel:${CONTACT_INFO.phone}`}
              className="flex items-center gap-3 group"
              style={{ textDecoration: 'none' }}
            >
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'rgba(181, 152, 90, 0.1)',
                }}
              >
                <Phone size={15} strokeWidth={1.5} style={{ color: '#B5985A' }} />
              </div>
              <span className="font-body text-sm" style={{ color: '#A3A3A3' }}>
                {CONTACT_INFO.phone}
              </span>
            </a>

            <a
              href={`mailto:${CONTACT_INFO.email}`}
              className="flex items-center gap-3 group"
              style={{ textDecoration: 'none' }}
            >
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'rgba(181, 152, 90, 0.1)',
                }}
              >
                <Mail size={15} strokeWidth={1.5} style={{ color: '#B5985A' }} />
              </div>
              <span className="font-body text-sm truncate" style={{ color: '#A3A3A3' }}>
                {CONTACT_INFO.email}
              </span>
            </a>

            <a
              href={CONTACT_INFO.address}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 group"
              style={{ textDecoration: 'none' }}
            >
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'rgba(181, 152, 90, 0.1)',
                }}
              >
                <MapPin size={15} strokeWidth={1.5} style={{ color: '#B5985A' }} />
              </div>
              <span className="font-body text-sm" style={{ color: '#A3A3A3' }}>
                {CONTACT_INFO.addressText}
              </span>
            </a>
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-2 gap-3">
            <a
              href={CONTACT_INFO.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 py-3 rounded-xl font-cta text-sm uppercase tracking-wider"
              style={{
                background: 'linear-gradient(135deg, #25D366, #128C7E)',
                color: '#FFFFFF',
                textDecoration: 'none',
                fontWeight: 600,
                letterSpacing: '0.05em',
              }}
              data-testid="footer-whatsapp"
            >
              <MessageCircle size={16} strokeWidth={2} />
              WhatsApp
            </a>

            <a
              href={CONTACT_INFO.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 py-3 rounded-xl font-cta text-sm uppercase tracking-wider"
              style={{
                background: 'rgba(181, 152, 90, 0.15)',
                border: '1px solid rgba(181, 152, 90, 0.4)',
                color: '#B5985A',
                textDecoration: 'none',
                fontWeight: 600,
                letterSpacing: '0.05em',
              }}
              data-testid="footer-website"
            >
              <Globe size={16} strokeWidth={2} />
              Imóveis
            </a>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <p
        className="font-body text-center leading-relaxed mt-6"
        style={{ fontSize: '0.65rem', color: '#555' }}
        data-testid="footer-disclaimer"
      >
        Os dados recolhidos serão usados exclusivamente para contacto comercial
        pela {AGENCY_NAME}. Pode solicitar a remoção a qualquer
        momento contactando {CONTACT_INFO.email}.
      </p>
    </footer>
  );
}
